import Cart from 'models/Cart';
import CartDisplay from 'features/Cart/components/CartDisplay';
import CartItem from 'models/CartItem';
import KioskLog from 'models/KioskLog';
import PropTypes from 'prop-types';
import React from 'react';
import { actions as cartActions } from 'redux/domain/Cart';
import { actions as notificationActions, SnackbarSettings } from 'redux/domain/Notification';
import { bindActionCreators } from 'redux';
import { cancelRequest, getCancelTokenSource } from 'api/Helpers';
import { connect } from 'react-redux';
import { List } from 'immutable';
import { SHOPPING_CART } from 'redux/domain/Cart/constants';
import withRouter from '../../../shared/utils/WithRouter';

class CartContainer extends React.Component {
	constructor() {
		super();

		this.cancelSource = getCancelTokenSource();
		this.handleDeleteAsync = this.handleDeleteAsync.bind(this);
		this.handleQuantityChangeAsync = this.handleQuantityChangeAsync.bind(this);
	}

	componentDidMount() {
		const { customerId, isGuest } = this.props;
		this.getItemsAsync(customerId, isGuest);
	}

	componentWillReceiveProps(nextProps) {
		const { clearUpdateMessages, customerId, disableNotification, showNotification } = this.props;

		// Customer has changed. Get their shopping cart or wishlist.
		if (nextProps.customerId !== customerId) {
			this.getItemsAsync(nextProps.customerId, nextProps.isGuest);
		}

		// We have message(s) to display from a cart update.
		if (nextProps.cartUpdateMessages.size > 0) {
			const settings = new SnackbarSettings(true, nextProps.cartUpdateMessages);
			// hide after 3 seconds
			settings.autoHideDuration = 3000;
			settings.onClose = disableNotification;
			showNotification(settings, [clearUpdateMessages]);
		}
	}

	componentWillUpdate(nextProps) {
		if (nextProps.fetchError) {
			// had to add this since errorPage was getting rendered multiple times
			if (this.props.router.location.pathname !== '/errorPage') {
				const log = new KioskLog(
					new Error('Fetch Error in CartContainer'),
					`Error updating cart/idea board - CustomerId: ${nextProps.customerId} - Referrer URL: ${
						this.props.router.location.pathname
					}`,
				);
				log.postMessage();
				this.props.router.navigate('/errorPage', { 
					state: { 
						text: `We couldn't update your cart or idea board.` 
					} 
				});
			}
		}
	}

	componentWillUnmount() {
		// Cancel any ongoing requests.
		cancelRequest(this.cancelSource);
	}

	/**
	 * Retrieve cart or wishlist products for a gien customer.
	 * @param {number} customerId - NOP customer Id
	 * @param {boolean} isGuest - guest user vs registered user
	 * 	Along with the cart type this determines whether or not we will request that
	 *  the backend first sync with STORIS.
	 */
	getItemsAsync(customerId, isGuest) {
		const { cartType, getCartItems, updateFetchError } = this.props;
		// request that the backend peform a sync when retrieving the shopping cart for the first
		// time for a logged in user.
		const requestSync = !isGuest && cartType === SHOPPING_CART;

		try {
			getCartItems(this.cancelSource.token, customerId, cartType, requestSync);
		} catch (e) {
			const log = new KioskLog(e, `customerId=${customerId} - isGuest=${isGuest}`);
			log.postMessage();
			updateFetchError(e);
		}
	}

	/**
	 * Delete a single shopping cart item.
	 * These will trigger a refetch for the updated cart.
	 * @param {number} cartId
	 */
	handleDeleteAsync(cartId) {
		const { customerId, deleteCartItem, updateUpdateError } = this.props;

		try {
			this.cancelSource = getCancelTokenSource();
			deleteCartItem(this.cancelSource.token, customerId, cartId, this.props.cartType);
		} catch (e) {
			const log = new KioskLog(e, `customerId=${customerId} - cartId=${cartId}`);
			log.postMessage();
			updateUpdateError(e);
		}
	}

	/**
	 * Update the quantity on a single cart item.
	 * @param {event} event
	 * @param {number} value
	 * @param {CartItem} cartItem
	 */
	handleQuantityChangeAsync(event, cartItem) {
		const { cartType, customerId, updateCartItem, updateUpdateError } = this.props;

		const updatedCartItem = Object.assign(new CartItem(), cartItem, {
			quantity: event.target.value,
		});

		try {
			this.cancelSource = getCancelTokenSource();
			updateCartItem(this.cancelSource.token, customerId, updatedCartItem, cartType);
		} catch (e) {
			const log = new KioskLog(
				e,
				`customerId=${customerId} - value=${event.target.value} - cartItem=${cartItem.cartId}`,
			);
			log.postMessage();
			updateUpdateError(e);
		}
	}

	render() {
		const {
			shoppingCart,
			shoppingCartQuantity,
			isFetching,
			cartType,
			wishList,
			openEmailCart,
		} = this.props;

		return (
			<div>
				<CartDisplay
					cart={cartType === SHOPPING_CART ? shoppingCart : wishList}
					isFetching={isFetching}
					handleDelete={this.handleDeleteAsync}
					handleQuantityChange={this.handleQuantityChangeAsync}
					totalPrice={
						cartType === SHOPPING_CART ? shoppingCart.sumTotalPrice() : wishList.sumTotalPrice()
					}
					totalItems={
						cartType === SHOPPING_CART ? shoppingCartQuantity : wishList.sumTotalQuantity()
					}
					openEmailCart={openEmailCart}
					cartType={cartType}
				/>
			</div>
		);
	}
}

CartContainer.propTypes = {
	customerId: PropTypes.number.isRequired,
	cartType: PropTypes.number,
	// eslint-disable-next-line react/no-unused-prop-types
	cartUpdateMessages: PropTypes.instanceOf(List).isRequired, // used but only as nextProps
	clearUpdateMessages: PropTypes.func.isRequired,
	deleteCartItem: PropTypes.func.isRequired,
	disableNotification: PropTypes.func.isRequired,
	getCartItems: PropTypes.func.isRequired,
	isGuest: PropTypes.bool.isRequired,
	shoppingCart: PropTypes.instanceOf(Cart),
	shoppingCartQuantity: PropTypes.number.isRequired,
	wishList: PropTypes.instanceOf(Cart),
	isFetching: PropTypes.bool.isRequired,
	showNotification: PropTypes.func.isRequired,
	updateCartItem: PropTypes.func.isRequired,
	updateFetchError: PropTypes.func.isRequired,
	updateUpdateError: PropTypes.func.isRequired,
	openEmailCart: PropTypes.func.isRequired,
};

CartContainer.defaultProps = {
	cartType: SHOPPING_CART,
	shoppingCart: new Cart(),
	wishList: new Cart(),
};

const mapDispatchToProps = dispatch =>
	bindActionCreators({ ...cartActions, ...notificationActions }, dispatch);

const mapStateToProps = state => {
	// Immutable List
	const cartUpdateMessages = state.cart.get('cartUpdateMessages');
	const isFetching = state.cart.get('isFetching');
	const fetchError = state.cart.get('fetchError');
	const shoppingCart = state.cart.get('shoppingCart');
	const shoppingCartQuantity = state.cart.get('shoppingCartQuantity');
	const wishList = state.cart.get('wishList');
	return {
		cartUpdateMessages,
		isFetching,
		fetchError,
		shoppingCart,
		shoppingCartQuantity,
		wishList,
	};
};

// for testing purposes
export { CartContainer as CartContainerClass };

// connect returns a function that accepts a component
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CartContainer));
